import { Main as Container } from 'Components/Main';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { deleteGapAnalysisVinculo, getGapAnalysisDetails } from '../../../services/gap-analysis';
import { Dialog, PageHeader, Select, TextField, Button, UnlinkSvg } from '../../../Components/ui';
import { typeLabelKeys, statusControl, maturityLevelKeys, translateDomain, sortNumeric, sortMaturityLevels } from '../../../utils/labels_utils';
import { SnackbarAlert } from 'Components/common/SnackbarAlert';
import styled from 'styled-components';

const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';
const BLUE_DEEP = 'var(--color-brand-strong)';
const BG = 'var(--vo-mist)';
const BORDER = 'var(--vo-line)';
const DIVIDER = 'var(--vo-cloud)';

/* ── Page header ── */
const BtnGroup = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-shrink: 0;
`;

/* ── Legend card ── */
const InfoCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 16px 20px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  border-left: 4px solid var(--vo-capri);
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
`;
const LegendTitle = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 8px;
`;
const LegendItems = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;
const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;
const LegendSquare = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 3px;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;
const LegendItemName = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${INK};
`;
const LegendItemDesc = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${MUTED};
`;

/* ── Progress bar card ── */
const ProgressCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 20px 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  margin-bottom: 16px;
`;
const SectionLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: .08em;
  margin: 0 0 14px;
`;
const ProgressBarWrap = styled.div`
  display: flex;
  gap: 1px;
  border-radius: 8px;
  overflow: hidden;
  height: 28px;
  margin-bottom: 12px;
`;
const ProgressSegment = styled.div`
  flex: ${({ $flex }) => $flex};
  background: ${({ $color }) => $color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Maven Pro', sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: ${({ $light }) => $light ? MUTED : 'white'};
  min-width: 0;
  overflow: hidden;
`;
const ProgressLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
`;
const ProgressLegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${MUTED};
`;
const LegendDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

/* ── Filters card ── */
const FiltersCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 20px 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  margin-bottom: 16px;
`;
const FilterGrid4 = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 12px;
`;
const FilterGrid3 = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 16px;
`;
const FilterFieldWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;
const FilterLabel = styled.label`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: ${MUTED};
`;
const FilterBtns = styled.div`
  display: flex;
  gap: 10px;
`;
const ClearBtn = styled.button`
  height: 40px;
  background: white;
  color: ${MUTED};
  border: 1.5px solid ${BORDER};
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: ${BG}; }
`;

/* ── Controls table ── */
const CtrlCard = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  overflow: hidden;
`;
const CtrlCardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid ${DIVIDER};
`;
const CtrlCardTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: ${INK};
  margin: 0;
`;
const CtrlTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;
const CtrlTh = styled.th`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: .08em;
  padding: 11px 16px;
  text-align: ${({ $center }) => $center ? 'center' : 'left'};
  border-bottom: 1px solid ${DIVIDER};
  background: ${BG};
`;
const ParentTr = styled.tr`
  border-bottom: 1px solid var(--vo-mist);
  background: var(--vo-mist);
  &:hover { background: var(--vo-mist); }
`;
const ExpandBtn = styled.button`
  width: 22px;
  height: 22px;
  border: 1px solid ${BORDER};
  background: white;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${MUTED};
  padding: 0;
`;
const CtrlTd = styled.td`
  padding: 11px 16px;
`;
const CodeTd = styled(CtrlTd)`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: var(--vo-steel);
`;
const NameTd = styled(CtrlTd)`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${INK};
  font-weight: 500;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const DomainChip = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${BLUE_DEEP};
  background: var(--color-brand-8);
  padding: 2px 9px;
  border-radius: 10px;
`;
const WeightTd = styled(CtrlTd)`
  text-align: center;
  font-family: 'Rubik', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: var(--vo-ink-2);
`;
const LinkedChip = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 9px;
  border-radius: 10px;
  background: ${({ $linked }) => $linked ? 'var(--vo-success-10)' : BG};
  color: ${({ $linked }) => $linked ? 'var(--vo-success)' : MUTED};
`;
const ActionTd = styled(CtrlTd)`
  text-align: center;
`;

/* ── Entity sub-rows ── */
const SubHeaderTr = styled.tr`
  background: var(--vo-capri-50);
  border-bottom: 1px solid var(--vo-capri-100);
`;
const SubTh = styled.td`
  padding: 7px 16px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: .08em;
  text-align: ${({ $center }) => $center ? 'center' : 'left'};
`;
const EntityTr = styled.tr`
  border-bottom: 1px solid var(--vo-capri-50);
  background: var(--vo-capri-50);
  &:hover { background: var(--vo-capri-50); }
`;
const EntityDot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--vo-steel);
  flex-shrink: 0;
`;
const TypeChip = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${BLUE_DEEP};
  background: var(--color-brand-8);
  padding: 2px 8px;
  border-radius: 10px;
`;
const StatusDotEnt = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const EMPTY_FILTERS = { peso: '', dominio: '', vinculo: '', entidadeTipo: '', entidadeNome: '', status: '', maturityLevel: '' };

export const GapAnalysisShow = () => {
  const { t } = useTranslation(['gap-analysis', 'common']);
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [itemToDelete, setItemToDelete] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [openIds, setOpenIds] = useState([]);
  const [tableSearch, setTableSearch] = useState('');

  const { isLoading, error, refetch, data: gapAnalysis } = useQuery('getGapAnalysisDetails', async () => {
    const data = await getGapAnalysisDetails(id);
    return data.status === 200 ? data.data : null;
  });

  useEffect(() => {
    const linkResult = location.state?.linkResult;
    if (!linkResult) return;

    const severityByCode = {
      GA_LINK_CREATED: 'success',
      GA_LINK_ALREADY_EXISTS: 'warning',
      GA_LINK_PARTIAL: 'warning',
      GA_LINK_ERROR: 'error',
    };
    const messageKeyByCode = {
      GA_LINK_CREATED: 'gap-analysis:controlsLink.messages.created',
      GA_LINK_ALREADY_EXISTS: 'gap-analysis:controlsLink.messages.alreadyLinked',
      GA_LINK_PARTIAL: 'gap-analysis:controlsLink.messages.partial',
      GA_LINK_ERROR: 'gap-analysis:controlsLink.messages.error',
    };

    setSnackbar({
      open: true,
      message: t(messageKeyByCode[linkResult.code] || messageKeyByCode.GA_LINK_ERROR),
      severity: severityByCode[linkResult.code] || 'error',
    });
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  if (isLoading) return <p>{t('common:labels.loading')}</p>;
  if (error) return <p>{t('gap-analysis:controlsLink.error')}</p>;

  const formattedStatuses = Object.keys(statusControl).map(status => ({
    label: t(statusControl[status].labelKey),
    count: gapAnalysis.statusCounts[status] || 0,
    color: statusControl[status].color,
  }));

  const totalCount = formattedStatuses.reduce((acc, s) => acc + s.count, 0);

  const uniqueValues = (key) => [...new Set(gapAnalysis.controls.map(control => control[key]))].sort();

  const getUniqueEntities = () => {
    const entityMap = new Map();
    const entityTypes = new Set();
    const entityStatuses = new Set();
    const maturityLevels = new Set();

    gapAnalysis.controls.forEach(control => {
      control.entities.forEach(entity => {
        const key = `${entity.entity_id}-${entity.entity_type}`;
        if (!entityMap.has(key)) {
          entityMap.set(key, { entity_id: entity.entity_id, entity_name: entity.entity_name });
        }
        entityTypes.add(entity.entity_type);
        entityStatuses.add(entity.status);
        maturityLevels.add(entity.maturity_level);
      });
    });

    return {
      entities: Array.from(entityMap.values()),
      entityTypes: Array.from(entityTypes),
      entityStatuses: Array.from(entityStatuses),
      maturityLevels: sortMaturityLevels(Array.from(maturityLevels)),
    };
  };

  const { entities, entityTypes, entityStatuses, maturityLevels } = getUniqueEntities();

  const handleFilterChange = (field, value) => setFilters(prev => ({ ...prev, [field]: value }));

  // '' means genuinely no selection; 'all' is the real, explicit "All" menu
  // choice — both mean "don't filter on this field," distinct only in how
  // the Select renders them (muted placeholder vs. full-strength value).
  const isFilterActive = (value) => value && value !== 'all';

  const filteredControls = gapAnalysis.controls
    .map(control => {
      const filteredEntities = isFilterActive(filters.entidadeNome) || isFilterActive(filters.entidadeTipo) || isFilterActive(filters.maturityLevel) || isFilterActive(filters.status)
        ? control.entities.filter(entity => (
          (isFilterActive(filters.entidadeTipo) ? entity.entity_type === filters.entidadeTipo : true) &&
          (isFilterActive(filters.entidadeNome) ? entity.entity_id === filters.entidadeNome : true) &&
          (isFilterActive(filters.maturityLevel) ? entity.maturity_level === filters.maturityLevel : true) &&
          (isFilterActive(filters.status) ? entity.status === filters.status : true)
        ))
        : control.entities;
      return { ...control, entities: filteredEntities };
    })
    .filter(control =>
      (control.entities.length > 0 || !(isFilterActive(filters.entidadeNome) || isFilterActive(filters.entidadeTipo) || isFilterActive(filters.maturityLevel) || isFilterActive(filters.status))) &&
      (isFilterActive(filters.peso) ? parseInt(control.controlWeighting, 10) === parseInt(filters.peso, 10) : true) &&
      (isFilterActive(filters.dominio) ? control.scfDomain === filters.dominio : true) &&
      (isFilterActive(filters.vinculo) ? control.vinculado.toString() === filters.vinculo : true)
    );

  const searchLower = tableSearch.toLowerCase();
  const displayControls = tableSearch
    ? filteredControls.filter(c =>
        (c.scfId ?? '').toLowerCase().includes(searchLower) ||
        (c.scfControl ?? '').toLowerCase().includes(searchLower)
      )
    : filteredControls;

  const toggleRow = (rowId) =>
    setOpenIds(prev => prev.includes(rowId) ? prev.filter(x => x !== rowId) : [...prev, rowId]);

  const onDelete = item => { setItemToDelete(item); setOpenDeleteModal(true); };
  const onCloseDeleteModal = () => { setOpenDeleteModal(false); setItemToDelete(null); };

  const onConfirmDeleteModal = async () => {
    try {
      await deleteGapAnalysisVinculo(itemToDelete._id, id);
      setSnackbar({ open: true, message: t('gap-analysis:show.messages.deleteSuccess'), severity: 'success' });
      refetch();
      onCloseDeleteModal();
    } catch (err) {
      onCloseDeleteModal();
      setSnackbar({
        open: true,
        message: err.response?.data?.message ?? t('gap-analysis:show.messages.deleteError'),
        severity: 'error',
      });
    }
  };

  return (
    <Container>
      {/* ── Page header ── */}
      <PageHeader
        title={gapAnalysis.gapAnalysis?.name ?? ''}
        segments={[
          { label: t('common:breadcrumbs.analysis') },
          { label: t('gap-analysis:page.title'), route: '/gap-analysis' },
          { label: gapAnalysis.gapAnalysis?.name ?? '' },
        ]}
        actions={
          <BtnGroup>
            <Button
              variant="primary"
              type="button"
              onClick={() => navigate('vincular')}
              startIcon={
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              }
              sx={{ height: '40px', padding: '0 18px' }}
            >
              {t('gap-analysis:show.linkControlsButton')}
            </Button>
            <Button
              variant="outline"
              type="button"
              onClick={() => navigate(`/gap-analysis/dashboard/${id}`)}
              startIcon={
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
                </svg>
              }
              sx={{ height: '40px', padding: '0 18px' }}
            >
              {t('gap-analysis:show.dashboardButton')}
            </Button>
          </BtnGroup>
        }
      />

      {/* ── Legend card ── */}
      <InfoCard>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--vo-capri)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
          <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <div>
          <LegendTitle>{t('gap-analysis:show.legendTitle')}</LegendTitle>
          <LegendItems>
            {Object.entries(statusControl).map(([key, { labelKey, color, descriptionKey }]) => (
              <LegendItem key={key}>
                <LegendSquare $color={color} />
                <LegendItemName>{t(labelKey)}</LegendItemName>
                <LegendItemDesc>— {t(descriptionKey)}</LegendItemDesc>
              </LegendItem>
            ))}
          </LegendItems>
        </div>
      </InfoCard>

      {/* ── Progress bar card ── */}
      <ProgressCard>
        <SectionLabel>{t('gap-analysis:show.progressSection')}</SectionLabel>
        {totalCount > 0 && (
          <ProgressBarWrap>
            {formattedStatuses
              .filter(s => s.count > 0)
              .map(({ label, count, color }) => {
                const pct = Math.round((count / totalCount) * 100);
                const isLight = color === 'var(--vo-line)' || color === 'var(--vo-cloud)';
                return (
                  <ProgressSegment key={label} $flex={count} $color={color} $light={isLight}>
                    {pct >= 6 ? `${pct}%` : ''}
                  </ProgressSegment>
                );
              })}
          </ProgressBarWrap>
        )}
        <ProgressLegend>
          {formattedStatuses.map(({ label, count, color }) => (
            <ProgressLegendItem key={label}>
              <LegendDot $color={color} />
              {label} ({count})
            </ProgressLegendItem>
          ))}
        </ProgressLegend>
      </ProgressCard>

      {/* ── Filters card ── */}
      <FiltersCard>
        <SectionLabel>{t('gap-analysis:show.filtersSection')}</SectionLabel>
        <FilterGrid4>
          <FilterFieldWrap>
            <FilterLabel>{t('gap-analysis:show.filters.domain')}</FilterLabel>
            <Select
              name="dominio"
              value={filters.dominio}
              onChange={value => handleFilterChange('dominio', value)}
              options={[
                { value: '', label: t('gap-analysis:show.filters.selectDomain') },
                { value: 'all', label: t('gap-analysis:show.filters.all') },
                ...uniqueValues('scfDomain').map(value => ({ value, label: translateDomain(t, value) })),
              ]}
            />
          </FilterFieldWrap>
          <FilterFieldWrap>
            <FilterLabel>{t('gap-analysis:show.filters.link')}</FilterLabel>
            <Select
              name="vinculo"
              value={filters.vinculo}
              onChange={value => handleFilterChange('vinculo', value)}
              options={[
                { value: '', label: t('gap-analysis:show.filters.selectLink') },
                { value: 'all', label: t('gap-analysis:show.filters.all') },
                { value: 'true', label: t('common:boolean.yes') },
                { value: 'false', label: t('common:boolean.no') },
              ]}
            />
          </FilterFieldWrap>
          <FilterFieldWrap>
            <FilterLabel>{t('common:labels.status')}</FilterLabel>
            <Select
              name="status"
              value={filters.status}
              onChange={value => handleFilterChange('status', value)}
              options={[
                { value: '', label: t('gap-analysis:show.filters.selectStatus') },
                { value: 'all', label: t('gap-analysis:show.filters.all') },
                ...entityStatuses.map(status => ({ value: status, label: statusControl[status] ? t(statusControl[status].labelKey) : status })),
              ]}
            />
          </FilterFieldWrap>
          <FilterFieldWrap>
            <FilterLabel>{t('gap-analysis:show.filters.entityName')}</FilterLabel>
            <Select
              name="entidadeNome"
              value={filters.entidadeNome}
              onChange={value => handleFilterChange('entidadeNome', value)}
              options={[
                { value: '', label: t('gap-analysis:show.filters.selectEntityName') },
                { value: 'all', label: t('gap-analysis:show.filters.allF') },
                ...entities.map(entity => ({ value: entity.entity_id, label: entity.entity_name })),
              ]}
            />
          </FilterFieldWrap>
        </FilterGrid4>
        <FilterGrid3>
          <FilterFieldWrap>
            <FilterLabel>{t('gap-analysis:show.filters.entityType')}</FilterLabel>
            <Select
              name="entidadeTipo"
              value={filters.entidadeTipo}
              onChange={value => handleFilterChange('entidadeTipo', value)}
              options={[
                { value: '', label: t('gap-analysis:show.filters.selectEntityType') },
                { value: 'all', label: t('gap-analysis:show.filters.all') },
                ...entityTypes.map(type => ({ value: type, label: typeLabelKeys[type] ? t(typeLabelKeys[type]) : type })),
              ]}
            />
          </FilterFieldWrap>
          <FilterFieldWrap>
            <FilterLabel>{t('gap-analysis:show.filters.maturityLevel')}</FilterLabel>
            <Select
              name="maturityLevel"
              value={filters.maturityLevel}
              onChange={value => handleFilterChange('maturityLevel', value)}
              options={[
                { value: '', label: t('gap-analysis:show.filters.selectMaturityLevel') },
                { value: 'all', label: t('gap-analysis:show.filters.all') },
                ...maturityLevels.map(level => ({ value: level, label: maturityLevelKeys[level] ? t(maturityLevelKeys[level]) : level })),
              ]}
            />
          </FilterFieldWrap>
          <FilterFieldWrap>
            <FilterLabel>{t('gap-analysis:show.filters.controlWeight')}</FilterLabel>
            <Select
              name="peso"
              value={filters.peso}
              onChange={value => handleFilterChange('peso', value)}
              options={[
                { value: '', label: t('gap-analysis:show.filters.selectControlWeight') },
                { value: 'all', label: t('gap-analysis:show.filters.all') },
                ...sortNumeric(uniqueValues('controlWeighting')).map(value => ({ value, label: value })),
              ]}
            />
          </FilterFieldWrap>
        </FilterGrid3>
        <FilterBtns>
          <ClearBtn type="button" onClick={() => setFilters(EMPTY_FILTERS)}>
            {t('gap-analysis:show.clearButton')}
          </ClearBtn>
          <Button
            variant="primary"
            type="button"
            startIcon={
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            }
            sx={{ height: '40px', padding: '0 20px' }}
          >
            {t('gap-analysis:show.filterButton')}
          </Button>
        </FilterBtns>
      </FiltersCard>

      {/* ── Controls table ── */}
      <CtrlCard>
        <CtrlCardHead>
          <CtrlCardTitle>
            {t('gap-analysis:show.controlsTitle')} ({displayControls.length})
          </CtrlCardTitle>
          <TextField
            placeholder={t('gap-analysis:show.searchPlaceholder')}
            value={tableSearch}
            onChange={e => setTableSearch(e.target.value)}
            sx={{ width: 220 }}
          />
        </CtrlCardHead>
        <CtrlTable>
          <thead>
            <tr>
              <CtrlTh style={{ width: 28 }} />
              <CtrlTh>{t('gap-analysis:table.controlCode')}</CtrlTh>
              <CtrlTh>{t('gap-analysis:table.control')}</CtrlTh>
              <CtrlTh>{t('gap-analysis:table.domain')}</CtrlTh>
              <CtrlTh $center>{t('gap-analysis:table.controlWeight')}</CtrlTh>
              <CtrlTh $center>{t('gap-analysis:table.link')}</CtrlTh>
              <CtrlTh $center>{t('gap-analysis:table.actions')}</CtrlTh>
            </tr>
          </thead>
          <tbody>
            {displayControls.map(control => {
              const rowId = control._id ?? control.scfId;
              const isOpen = openIds.includes(rowId);
              return (
                <React.Fragment key={rowId}>
                  <ParentTr>
                    <CtrlTd style={{ textAlign: 'center', width: 28 }}>
                      <ExpandBtn type="button" onClick={() => toggleRow(rowId)}>
                        <svg
                          width="11" height="11" viewBox="0 0 24 24" fill="none"
                          stroke="currentColor" strokeWidth="2.5"
                          style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}
                        >
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </ExpandBtn>
                    </CtrlTd>
                    <CodeTd>{control.scfId}</CodeTd>
                    <NameTd title={control.scfControl}>{control.scfControl}</NameTd>
                    <CtrlTd>
                      <DomainChip>{translateDomain(t, control.scfDomain)}</DomainChip>
                    </CtrlTd>
                    <WeightTd>{control.controlWeighting}</WeightTd>
                    <ActionTd>
                      <LinkedChip $linked={control.vinculado}>
                        {control.vinculado ? t('common:boolean.yes') : t('common:boolean.no')}
                      </LinkedChip>
                    </ActionTd>
                    <ActionTd>
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <Button iconOnly variant="icon" title={t('gap-analysis:controlLink.title')} onClick={() => navigate(`/gap-analysis/${id}/vincular/${control._id}`)}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                          </svg>
                        </Button>
                      </div>
                    </ActionTd>
                  </ParentTr>

                  {isOpen && (
                    <>
                      <SubHeaderTr>
                        <SubTh />
                        <SubTh colSpan={2}>{t('gap-analysis:show.filters.entityName')}</SubTh>
                        <SubTh>{t('common:labels.type')}</SubTh>
                        <SubTh $center>{t('common:labels.status')}</SubTh>
                        <SubTh $center>{t('gap-analysis:table.maturityLevel')}</SubTh>
                        <SubTh $center>{t('gap-analysis:table.actions')}</SubTh>
                      </SubHeaderTr>
                      {control.entities.map(entity => (
                        <EntityTr key={entity._id ?? entity.entity_id}>
                          <CtrlTd />
                          <CtrlTd colSpan={2}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 8 }}>
                              <EntityDot />
                              <span style={{ fontFamily: "'Lato', sans-serif", fontSize: 13, color: 'var(--vo-ink-2)' }}>
                                {entity.entity_name}
                              </span>
                            </div>
                          </CtrlTd>
                          <CtrlTd>
                            <TypeChip>
                              {typeLabelKeys[entity.entity_type] ? t(typeLabelKeys[entity.entity_type]) : entity.entity_type}
                            </TypeChip>
                          </CtrlTd>
                          <ActionTd>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5, justifyContent: 'center' }}>
                              <StatusDotEnt $color={statusControl[entity.status]?.color ?? MUTED} />
                              <span style={{ fontFamily: "'Maven Pro', sans-serif", fontSize: 12, color: MUTED }}>
                                {statusControl[entity.status] ? t(statusControl[entity.status].labelKey) : entity.status}
                              </span>
                            </div>
                          </ActionTd>
                          <ActionTd style={{ fontFamily: "'Maven Pro', sans-serif", fontSize: 12, color: MUTED }}>
                            {maturityLevelKeys[entity.maturity_level] ? t(maturityLevelKeys[entity.maturity_level]) : entity.maturity_level}
                          </ActionTd>
                          <ActionTd>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                              <Button
                                iconOnly
                                variant="icon"
                                title={t('common:tooltips.view')}
                                onClick={() => navigate(`/gap-analysis/control/show/${entity._id}?gapanalysis_id=${id}`)}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
                                </svg>
                              </Button>
                              <Button iconOnly variant="icon" danger title={t('gap-analysis:show.unlinkTooltip')} onClick={() => onDelete(entity)}>
                                <UnlinkSvg />
                              </Button>
                            </div>
                          </ActionTd>
                        </EntityTr>
                      ))}
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </CtrlTable>
      </CtrlCard>

      <Dialog
        open={openDeleteModal}
        onConfirm={onConfirmDeleteModal}
        onCancel={onCloseDeleteModal}
        message={t('gap-analysis:show.confirmUnlink', { name: itemToDelete?.entity_name ?? '' })}
      />
      <SnackbarAlert
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </Container>
  );
};
